import { Box, Button } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import {
    Li,
    MenuListCustom,
    MenuStyled,
    SearchSites,
    ToolbarStyled,
    useClasses,
} from "./CustomerSiteSelector.styles";
import { useReduxState } from "../../../../../explorer/redux/hooks";
import { reduxStoreConfig } from "../../../../configuration/reduxStoreConfig";
import { Site } from "../../../../../explorer/types/SpareParts";
import { AppIconsPushpin, AppIconsShieldFilled } from "../../../AppIcons";
import { useTranslation } from "../../../../../explorer/hooks/useTranslation";
import SearchNoResults from "../../../SearchNoResults/SearchNoResults";
import SkeletonList from "../../../../pages/SpareParts/HomePage/SkeletonList/SkeletonList";
import useBrands from "../../../../../explorer/hooks/useBrands";
import useGoToPage from "../../../../hook/useGoToPage";
import { useLocation } from "react-router-dom";
import useCurrentSite from "../../../../hook/useCurrentSite";

function CustomerSiteSelector() {
    const [sites, setSites] = useReduxState<Site[]>(
        reduxStoreConfig.sparePartsSites
    );
    const [currentSite, setCurrentSite] = useCurrentSite();

    const inputRef = useRef<HTMLInputElement | null>(null);

    const { t } = useTranslation();

    const classes = useClasses();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [searchText, setSearchText] = useState<string | undefined>("");
    const [loading, setLoading] = useState<boolean>(true);
    const [sitesList, setSitesList] = useState<Site[]>(sites);
    const isMenuOpen = Boolean(anchorEl);
    const brand = useBrands().currentBrandUrl;
    const goToPage = useGoToPage();
    const location = useLocation();

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
        setTimeout(() => {
            if (inputRef.current) inputRef.current.focus();
        }, 100);
        resetSearch();
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSearchText(undefined);
    };

    useEffect(() => {
        setLoading(true);
        // Se undefined, deve resettare il filtro
        if (!searchText) resetSearch();
        else if (searchText?.trim() !== "" && searchText.trim().length > 2)
            search(searchText!.trim());

        setLoading(false);
    }, [searchText]);

    useEffect(() => {
        sortSites();
    }, [sites, currentSite]);

    const sortSites = () => {
        if (!sites) return;
        let sortedSites: Site[] = [...sites];
        if (currentSite) {
            sortedSites.sort((a, b) => {
                if (a.customerSiteCode === currentSite.customerSiteCode)
                    return -1;
                else if (b.customerSiteCode === currentSite.customerSiteCode)
                    return 1;
                else
                    return (
                        a.customerSiteCode?.localeCompare(
                            b.customerSiteCode ?? ""
                        ) ?? 0
                    );
            });
        } else {
            sortedSites.sort((a, b) => {
                return (
                    a.customerSiteCode?.localeCompare(
                        b.customerSiteCode ?? ""
                    ) ?? 0
                );
            });
        }
        setSitesList(sortedSites);
    };

    const search = async (val: string) => {
        setSitesList(
            sites.filter((site) => {
                const text = `${site?.customerSiteCode}-${
                    site?.customerSiteName
                } ${site?.address} ${site?.address && "-"} ${site?.city}, ${
                    site?.zipCode
                } (${site?.country})`;

                return text.toUpperCase().includes(val.toUpperCase());
            })
        );
    };

    const resetSearch = async () => {
        sortSites();
    };

    const onSearchHandle = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        event.stopPropagation();
        setSearchText(event.target.value);
    };

    const onClickSiteHandle = (site: Site) => {
        if (
            location.pathname.split("/").at(-1) !== "basket" &&
            location.pathname.split("/").at(-1) !== "offerOrderHistory"
        ) {
            goToPage.spareParts.goToInstalledBase(site.customerSiteCode!);
        } else {
            setCurrentSite(site);
        }
        handleMenuClose();
    };

    const menuId = "primary-search-account-menu";
    const renderMenu = (
        <MenuStyled
            className={"AppSiteSelector-menu"}
            sx={classes.list}
            anchorEl={anchorEl}
            anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
            }}
            id={menuId}
            keepMounted
            transformOrigin={{
                vertical: "top",
                horizontal: "left",
            }}
            open={isMenuOpen}
            onClose={handleMenuClose}
        >
            <ToolbarStyled
                aria-hidden="true"
                tabIndex={-1}
                children={isMenuOpen &&
                    <SearchSites
                        type="text"
                        label={t("common.homepagesearchbar.searchSites")}
                        variant="standard"
                        fullWidth
                        value={searchText || ""}
                        inputRef={(input) => {
                            if (input) inputRef.current = input;
                        }}
                        onChange={onSearchHandle}
                        onKeyDown={(e) => e.stopPropagation()}
                    />
                }
            />
            <MenuListCustom>
                {loading ? (
                    <SkeletonList mkey={"siteSelector"} rows={4} />
                ) : sitesList.length > 0 ? (
                    sitesList.slice(0, 100).map((site: Site, idx: number) => (
                        <Li
                            className={
                                currentSite?.customerSiteCode ===
                                site?.customerSiteCode
                                    ? "selected"
                                    : ""
                            }
                            key={idx}
                            onClick={() => {
                                onClickSiteHandle(site);
                            }}
                        >
                            {currentSite?.customerSiteCode ===
                            site?.customerSiteCode ? (
                                <AppIconsShieldFilled />
                            ) : (
                                ""
                            )}
                            <Box>
                                <div>
                                    {`${site?.customerSiteCode} - ${site?.customerSiteName}`}{" "}
                                    <br />
                                    {`${site?.address} ${site?.address && "-"}
                                    ${site?.city}, ${site.zipCode} (${
                                        site?.country
                                    })`}
                                </div>
                            </Box>
                        </Li>
                    ))
                ) : (
                    <SearchNoResults />
                )}
            </MenuListCustom>
        </MenuStyled>
    );

    const renderButton = (
        handleClick: (event: React.MouseEvent<HTMLElement>) => void
    ) => (
        <Button
            sx={classes.siteSelectorButton}
            className="AppSiteSelector-icon"
            aria-label="Basket"
            aria-controls={menuId}
            aria-haspopup="true"
            onClick={handleClick}
            color="inherit"
            startIcon={<AppIconsPushpin />}
        >
            <span>
                {/* {siteSelected?.customerSiteCode} -
                {siteSelected?.customerSiteName} <br /> {siteSelected?.address}-
                {siteSelected?.city}, {siteSelected?.zipCode} (
                {siteSelected?.country}) */}
                {currentSite?.customerSiteCode}
                <br />
                {currentSite?.customerSiteName}
            </span>
        </Button>
    );

    return (
        <React.Fragment>
            {sites && sites.length > 1 ? (
                <>
                    {renderButton(handleMenuOpen)}
                    {sites && sites.length > 1 && renderMenu}
                </>
            ) : (
                <Box>
                    {renderButton(() => {
                        onClickSiteHandle(currentSite);
                    })}
                </Box>
            )}
        </React.Fragment>
    );
}
export default CustomerSiteSelector;
